import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * #1361 「戻る」の共通実装。
 *
 * ## なぜ必要か
 *
 * 戻るボタンを `navigate('/stores/X')` のように**行き先を指定して push** すると、
 * 履歴が減らずに 1 件ずつ増える。押すたびに積み上がるので、いくら戻っても
 * 最初の画面に到達できない:
 *
 * ```
 * /stores/X → /stores/X/courses/Y →（戻る=push）→ /stores/X
 *           → /stores/X/courses/Y →（戻る=push）→ /stores/X → …
 * ```
 *
 * したがって既定は `navigate(-1)`（履歴を 1 つ戻す）にする。
 *
 * ## fallback が要る理由
 *
 * QR やシェア URL から**直接その画面に着地した場合、戻り先が無い**。
 * `navigate(-1)` はアプリの外（QR リーダーや直前のサイト）へ出てしまい、
 * 客がアプリに戻れなくなる。訪日外国人の one-time 客は QR 直着地が主経路なので、
 * ここは実際に踏まれる。
 *
 * そのため「このアプリ内で遷移してきたか」を見て、来ていなければ fallback へ
 * **replace** で移す（replace なので、そこからさらに戻ってもループしない）。
 *
 * @param fallback 直リンク着地時の遷移先
 */
export function useGoBack(fallback: string) {
  const navigate = useNavigate()
  return useCallback(() => {
    // `idx` は React Router が history.state に持つアプリ内の遷移回数。
    // 0 or 未定義 = このアプリで最初に開いた画面 = 戻り先が無い。
    const idx = (window.history.state as { idx?: number } | null)?.idx
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1)
      return
    }
    navigate(fallback, { replace: true })
  }, [navigate, fallback])
}
